'use strict';

/**
 * In-place re-authentication modal.
 *
 * When a session expires while the page stays loaded, the Fable RestClient's
 * authenticationRecovery hook calls ensure() on this view. Rather than throwing
 * the user back to the full-page sign-in (PictView-Auth) -- which loses their
 * route, scroll, and in-flight view state -- this drives a compact
 * pict-section-modal over the current page: username prefilled from the known
 * session, password (and the 2FA / recovery-code path when the org requires it)
 * captured in place, reusing AuthClient.login exactly as the full screen does.
 *
 * Most accounts do not have a password at all -- they sign in through an OAuth
 * provider (GitHub, Google, and whatever identity integrations come later), so
 * the password field alone left them stuck: cancelling out to the full sign-in
 * screen was the only way back in. The modal also lists whichever providers
 * this instance has configured (the same AppData.Auth.Providers the full
 * screen reads), each a plain link to AuthClient.beginURL(name, returnTo).
 * That is a real navigation -- OAuth has to leave the page for the provider's
 * consent screen -- but returnTo carries the current route through the round
 * trip, so the browser lands back where it left off, signed in, instead of on
 * the app's default landing page.
 *
 * ensure() resolves:
 *   true  -- signed back in; the cookie is refreshed, so the RestClient replays
 *            the original request and the user's click "just works".
 *   false -- cancelled, enrollment required, or gave up; the app falls back to
 *            the full showAuth('login') screen and the original 401 stands.
 *
 * Single-flight on this side too: a burst of 401s that all reach ensure() share
 * one modal (the RestClient already collapses them, but a proactive banner can
 * call ensure() independently, so we guard here as well).
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');
const libProviderLabels = require('../OAuth-Provider-Labels.js');

function _escapeHTML(pStr)
{
	return String(pStr == null ? '' : pStr)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

const _ViewConfiguration =
{
	ViewIdentifier: 'Plansheet-ReAuth',
	DefaultRenderable: 'Plansheet-ReAuth',
	DefaultDestinationAddress: '#Plansheet-ReAuth-Anchor',
	AutoRender: false,

	CSS: /*css*/`
		.psra-body { font-size: 13px; color: var(--cnx-txt, #eef2f7); }
		.psra-lead { font-size: 12.5px; color: var(--cnx-lbl, #9aabc0); margin: 0 0 12px; line-height: 1.5; }
		.psra-id { display: flex; align-items: center; gap: 8px; background: rgba(8,14,24,0.34); border: 1px solid var(--cnx-cellb, rgba(150,166,188,0.3)); border-radius: 8px; padding: 8px 11px; margin-bottom: 10px; }
		.psra-id-label { font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: var(--cnx-lbl, #9aabc0); }
		.psra-id-value { font-weight: 600; font-size: 13px; }
		.psra-label { font-size: 12px; color: var(--cnx-lbl, #9aabc0); display: block; margin-bottom: 4px; }
		.psra-input { width: 100%; box-sizing: border-box; margin-bottom: 6px; }
		.psra-error { color: #ff9d92; font-size: 12.5px; margin: 4px 0 8px; min-height: 1em; }
		.psra-alt { font-size: 12px; margin-top: 8px; display: flex; gap: 14px; }
		.psra-alt a { color: #f0c44a; cursor: pointer; }
		.psra-note { font-size: 12px; color: #8fd3a3; margin: 4px 0 0; min-height: 1em; }
		.psra-divider { text-align: center; margin: 14px 0 10px; color: var(--cnx-lbl, #9aabc0); font-size: 11px; position: relative; }
		.psra-divider::before, .psra-divider::after { content: ""; position: absolute; top: 50%; width: calc(50% - 20px); height: 1px; background: var(--cnx-cellb, rgba(150,166,188,0.3)); }
		.psra-divider::before { left: 0; }
		.psra-divider::after { right: 0; }
		.psra-providers { display: flex; flex-direction: column; gap: 8px; }
		.psra-provider { display: block; padding: 9px 12px; background: rgba(8,14,24,0.34); border: 1px solid var(--cnx-cellb, rgba(150,166,188,0.3)); border-radius: 8px; color: var(--cnx-txt, #eef2f7); text-decoration: none; font-size: 13px; text-align: center; font-weight: 500; }
		.psra-provider:hover { background: rgba(8,14,24,0.5); }
	`,

	Templates: [{ Hash: 'Plansheet-ReAuth', Template: /*html*/`<div></div>` }],
	Renderables:
	[
		{ RenderableHash: 'Plansheet-ReAuth', TemplateHash: 'Plansheet-ReAuth', ContentDestinationAddress: '#Plansheet-ReAuth-Anchor', RenderMethod: 'replace' }
	]
};

class PictViewReAuth extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		// Shared in-flight ensure() promise; see the single-flight note above.
		this._ensurePromise = null;
		// Last-known signed-in username. The proactive expiry check nulls
		// AppData.Auth.User on the way out, so we cannot rely on it being present
		// when ensure() runs -- remember it while we still know it.
		this._lastKnownUsername = '';
	}

	/**
	 * Stash the identity to prefill on the next re-auth. The app calls this while
	 * the session is live (login / boot), so a later checkSession that clears
	 * AppData.Auth.User cannot erase what we need to prefill the modal.
	 *
	 * @param {Object} pUserRecord - The signed-in user record (or null).
	 */
	rememberIdentity(pUserRecord)
	{
		let tmpUsername = pUserRecord
			? (pUserRecord.EMail || pUserRecord.Email || pUserRecord.LocalLogin || pUserRecord.UserName || pUserRecord.Name || '')
			: '';
		if (tmpUsername) { this._lastKnownUsername = tmpUsername; }
	}

	_modal() { return this.pict.views['Pict-Section-Modal']; }
	_authClient() { return this.pict.providers.AuthClient; }

	// The identity we re-authenticate. It is fixed to the expired session's user;
	// the modal cannot switch accounts (that is what the full sign-in screen is for).
	_username()
	{
		let tmpUser = (this.pict.AppData && this.pict.AppData.Auth && this.pict.AppData.Auth.User) || null;
		let tmpLive = tmpUser ? (tmpUser.EMail || tmpUser.Email || tmpUser.LocalLogin || tmpUser.UserName || tmpUser.Name || '') : '';
		if (tmpLive)
		{
			// Keep the remembered copy fresh whenever the live record is present.
			this._lastKnownUsername = tmpLive;
			return tmpLive;
		}
		// The proactive check may have nulled AppData.Auth.User; fall back to the
		// identity we stashed while signed in.
		return this._lastKnownUsername || '';
	}

	/**
	 * The recovery entry point wired into RestClient.authenticationRecovery.
	 * Returns a Promise<boolean>. Single-flight: concurrent callers share one modal.
	 */
	ensure()
	{
		if (this._ensurePromise) { return this._ensurePromise; }
		this._ensurePromise = this._runEnsure();
		// Clear on settle so a later, unrelated expiry re-arms a fresh modal.
		let fClear = () => { this._ensurePromise = null; };
		this._ensurePromise.then(fClear, fClear);
		return this._ensurePromise;
	}

	async _runEnsure()
	{
		let tmpUsername = this._username();
		// No known user (e.g. never signed in this load): nothing to prefill, so
		// let the full sign-in screen own it.
		if (!tmpUsername)
		{
			this._fallbackToFullSignIn();
			return false;
		}

		let tmpError = '';
		// Loop so a wrong password re-prompts in place instead of dropping the user.
		while (true)
		{
			let tmpOutcome = await this._showPasswordModal(tmpUsername, tmpError);
			if (tmpOutcome.action !== 'signin')
			{
				// Cancel / dismiss: hand off to the full screen, resolve false.
				this._fallbackToFullSignIn();
				return false;
			}
			if (!tmpOutcome.password)
			{
				tmpError = 'Enter your password to continue.';
				continue;
			}

			let tmpResult = await this._authClient().login(tmpUsername, tmpOutcome.password);
			if (tmpResult === true)
			{
				return true;
			}
			if (tmpResult === 'challenge')
			{
				let tmpVerified = await this._runChallenge();
				if (tmpVerified) { return true; }
				// Gave up on the code step: re-prompt the password once more.
				tmpError = 'Two-step verification was not completed. Try again.';
				continue;
			}
			if (tmpResult === 'enroll')
			{
				// A restricted session needing 2FA enrollment cannot be resolved in a
				// compact modal; the full screen carries the enrollment flow.
				this._fallbackToFullSignIn();
				return false;
			}
			// false: bad password (or other soft failure) -- show the error, loop.
			tmpError = (this.pict.AppData.Auth && this.pict.AppData.Auth.LastError) || 'That did not work. Check your password and try again.';
		}
	}

	// The address to send the browser back to once an OAuth round trip
	// completes -- the SPA route it was on, hash and all, re-validated
	// server-side (see beginURL / orator-authentication's _safeReturnTo).
	_returnTo()
	{
		if (typeof window === 'undefined' || !window.location) { return ''; }
		return window.location.pathname + window.location.search + window.location.hash;
	}

	// OAuth provider links for whichever identity providers this instance has
	// configured -- the same list the full sign-in screen offers, so an
	// account signed in with GitHub / Google / a future integration is not
	// stuck with only a password field it never set. A click is a real page
	// navigation (OAuth cannot happen in a modal); returnTo brings the
	// browser back to this route once it completes.
	_providersHTML()
	{
		let tmpProviders = (this.pict.AppData.Auth && this.pict.AppData.Auth.Providers) || [];
		if (!tmpProviders.length) { return ''; }
		let tmpReturnTo = this._returnTo();
		let tmpRows = tmpProviders.map((pProvider) =>
			'<a class="psra-provider" href="' + _escapeHTML(this._authClient().beginURL(pProvider.Name, tmpReturnTo)) + '">'
			+ 'Continue with ' + _escapeHTML(libProviderLabels.labelForProvider(pProvider.Name)) + '</a>').join('');
		return '<div class="psra-divider">or continue with</div><div class="psra-providers">' + tmpRows + '</div>';
	}

	// Compact password modal. Resolves { action:'signin'|'cancel', password }.
	_showPasswordModal(pUsername, pError)
	{
		let tmpContent =
			'<div class="psra-body">'
			+ '<p class="psra-lead">Your session expired. Sign back in to pick up right where you left off -- your page and unsaved work stay put.</p>'
			+ '<div class="psra-id"><span class="psra-id-label">Account</span><span class="psra-id-value">' + _escapeHTML(pUsername) + '</span></div>'
			+ '<label class="psra-label" for="ReAuth-Password">Password</label>'
			+ '<input id="ReAuth-Password" type="password" class="pict-input psra-input" autocomplete="current-password" autofocus>'
			+ '<div class="psra-error">' + _escapeHTML(pError || '') + '</div>'
			+ this._providersHTML()
			+ '</div>';
		let tmpDialog = this._modal().show(
		{
			title: 'Session expired',
			content: tmpContent,
			buttons:
			[
				{ Hash: 'cancel', Label: 'Sign in fully' },
				{ Hash: 'signin', Label: 'Sign back in', Style: 'primary' }
			]
		});
		// Focus + Enter-to-submit once the modal DOM is live.
		setTimeout(() =>
		{
			let tmpField = document.getElementById('ReAuth-Password');
			if (!tmpField) { return; }
			tmpField.focus();
			tmpField.onkeydown = (pEvent) =>
			{
				if (pEvent.key === 'Enter')
				{
					pEvent.preventDefault();
					this._clickModalButton('signin');
				}
			};
		}, 30);
		return tmpDialog.then((pChoice) =>
		{
			let tmpPassword = (document.getElementById('ReAuth-Password') || {}).value || '';
			return { action: (pChoice === 'signin') ? 'signin' : 'cancel', password: tmpPassword };
		});
	}

	/**
	 * Two-step verification loop. Reuses AuthClient.verifyLoginCode /
	 * useRecoveryCode / resendLoginCode against the pending challenge that
	 * login() staged on AppData.Auth.PendingChallenge. Resolves true (verified)
	 * or false (cancelled / gave up).
	 */
	async _runChallenge()
	{
		let tmpError = '';
		let tmpNote = '';
		let tmpRecoveryMode = false;
		while (true)
		{
			let tmpChallenge = (this.pict.AppData.Auth && this.pict.AppData.Auth.PendingChallenge) || {};
			let tmpOutcome = await this._showChallengeModal(tmpChallenge, tmpRecoveryMode, tmpError, tmpNote);
			tmpError = '';
			tmpNote = '';

			if (tmpOutcome.action === 'cancel')
			{
				return false;
			}
			if (tmpOutcome.action === 'toggle-recovery')
			{
				tmpRecoveryMode = !tmpRecoveryMode;
				continue;
			}
			if (tmpOutcome.action === 'resend')
			{
				let tmpResent = await this._authClient().resendLoginCode();
				tmpNote = tmpResent ? 'A new code is on its way.' : '';
				tmpError = tmpResent ? '' : ((this.pict.AppData.Auth && this.pict.AppData.Auth.LastError) || 'Could not resend the code.');
				continue;
			}
			// action === 'verify'
			if (!tmpOutcome.code)
			{
				tmpError = tmpRecoveryMode ? 'Enter one of your recovery codes.' : 'Enter the code we sent you.';
				continue;
			}
			let tmpVerified = tmpRecoveryMode
				? await this._authClient().useRecoveryCode(tmpOutcome.code)
				: await this._authClient().verifyLoginCode(tmpOutcome.code);
			if (tmpVerified === true) { return true; }
			tmpError = (this.pict.AppData.Auth && this.pict.AppData.Auth.LastError) || 'That code did not work.';
		}
	}

	// Resolves { action, code }.
	_showChallengeModal(pChallenge, pRecoveryMode, pError, pNote)
	{
		let tmpChannel = pChallenge.Channel || 'email';
		let tmpDestination = pChallenge.MaskedDestination || '';
		let tmpLead = pRecoveryMode
			? 'Enter one of the one-time recovery codes you saved when you set up two-step verification.'
			: ('We sent a verification code' + (tmpDestination ? (' to ' + _escapeHTML(tmpDestination)) : (' via ' + _escapeHTML(tmpChannel))) + '. Enter it to finish signing in.');
		let tmpContent =
			'<div class="psra-body">'
			+ '<p class="psra-lead">' + tmpLead + '</p>'
			+ '<label class="psra-label" for="ReAuth-Code">' + (pRecoveryMode ? 'Recovery code' : 'Verification code') + '</label>'
			+ '<input id="ReAuth-Code" type="text" inputmode="' + (pRecoveryMode ? 'text' : 'numeric') + '" class="pict-input psra-input" autocomplete="one-time-code" autofocus>'
			+ '<div class="psra-error">' + _escapeHTML(pError || '') + '</div>'
			+ '<div class="psra-note">' + _escapeHTML(pNote || '') + '</div>'
			+ '<div class="psra-alt">'
			+ (pRecoveryMode
				? '<a onclick="_Pict.views[\'Plansheet-ReAuth\']._modalAction(\'toggle-recovery\')">Use an emailed code instead</a>'
				: '<a onclick="_Pict.views[\'Plansheet-ReAuth\']._modalAction(\'resend\')">Resend code</a>'
					+ '<a onclick="_Pict.views[\'Plansheet-ReAuth\']._modalAction(\'toggle-recovery\')">Use a recovery code</a>')
			+ '</div>'
			+ '</div>';
		let tmpDialog = this._modal().show(
		{
			title: 'Two-step verification',
			content: tmpContent,
			buttons:
			[
				{ Hash: 'cancel', Label: 'Cancel' },
				{ Hash: 'verify', Label: 'Verify', Style: 'primary' }
			]
		});
		setTimeout(() =>
		{
			let tmpField = document.getElementById('ReAuth-Code');
			if (tmpField) { tmpField.focus(); }
		}, 30);
		return tmpDialog.then((pChoice) =>
		{
			// An alt-link click stashed an override action; honor it over the button.
			if (this._pendingAction)
			{
				let tmpAction = this._pendingAction;
				this._pendingAction = null;
				return { action: tmpAction, code: '' };
			}
			let tmpCode = ((document.getElementById('ReAuth-Code') || {}).value || '').trim();
			return { action: (pChoice === 'verify') ? 'verify' : 'cancel', code: tmpCode };
		});
	}

	// The alt-links (resend / recovery toggle) dismiss the current modal, then the
	// challenge loop reopens it in the requested mode. We stash the intent and
	// click the (harmless) cancel button so the modal promise settles; the
	// challenge modal's .then honors _pendingAction over the raw button hash.
	_modalAction(pAction)
	{
		this._pendingAction = pAction;
		this._clickModalButton('cancel');
	}

	// Programmatically activate a footer button on the topmost live modal dialog.
	// pict-section-modal wires each footer button as a [data-hash] click that
	// resolves the show() promise, so a synthetic click is the supported seam.
	_clickModalButton(pHash)
	{
		if (typeof document === 'undefined') { return; }
		let tmpDialogs = document.querySelectorAll('.pict-modal-dialog');
		let tmpDialog = tmpDialogs[tmpDialogs.length - 1];
		if (!tmpDialog) { return; }
		let tmpButton = tmpDialog.querySelector('[data-hash="' + pHash + '"]');
		if (tmpButton) { tmpButton.click(); }
	}

	// Deliberate handoff to the full-page sign-in. Kept in one place so both the
	// no-username and cancel/enroll paths route identically.
	_fallbackToFullSignIn()
	{
		let tmpApp = this.pict.PictApplication;
		if (tmpApp && typeof tmpApp.showAuth === 'function')
		{
			tmpApp.showAuth('login');
		}
	}
}

module.exports = PictViewReAuth;
module.exports.default_configuration = _ViewConfiguration;
